import { Save } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LocIcon from '@/assets/store/icon/loc-icon.svg';
import ConfirmModal from '@/domains/dashboard-management/components/ConfirmModal/ConfirmModal';
import AddressFields from '@/domains/mobile-web/components/AddressFields/AddressFields';
import InputMobile from '@/domains/mobile-web/components/InputMobile/InputMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import {
  fieldErrorOf,
  useAddresses,
  useSaveAddress,
  useSetDefaultAddress,
} from '@/domains/mobile-web/hooks/useAddresses';
import type {
  Address,
  AddressDraft,
  AddressDraftField,
} from '@/domains/mobile-web/types/address.types';
import {
  addressDraftErrors,
  addressDraftFieldOf,
  addressDraftOf,
  addressPlaceOf,
  EMPTY_ADDRESS_DRAFT,
} from '@/domains/mobile-web/utils/address';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import Button from '@/shared/components/ui/Button';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import { showToast } from '@/shared/utils/showToast';

type RecipientField = 'recipient_name' | 'phone_number';
type ErrorField = RecipientField | AddressDraftField;

const SettingAddress = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, isLoading, isError } = useAddresses();
  const address = id ? data?.rows.find((row) => row.id === id) : undefined;

  return (
    <main className="min-h-screen">
      <HeaderMobile>
        <TopBarHeader type="back" onBack={() => navigate(-1)} title="Pengaturan / Tambah Alamat" />
      </HeaderMobile>

      {!id ? (
        <AddressForm />
      ) : isLoading ? null : isError ? (
        <WrapperMobile>
          <p className="body1 text-text-grey">Gagal memuat alamat.</p>
        </WrapperMobile>
      ) : !address ? (
        <WrapperMobile>
          <p className="body1 text-text-grey">Alamat tidak ditemukan.</p>
        </WrapperMobile>
      ) : (
        <AddressForm key={address.id} address={address} />
      )}
    </main>
  );
};

const AddressForm = ({ address }: { address?: Address }) => {
  const navigate = useNavigate();
  const saveAddress = useSaveAddress();
  const setDefaultAddress = useSetDefaultAddress();

  const [recipient, setRecipient] = useState<Record<RecipientField, string>>({
    recipient_name: address?.recipient_name ?? '',
    phone_number: address?.phone_number ?? '',
  });
  const [place, setPlace] = useState<AddressDraft>(
    address ? addressDraftOf(address) : EMPTY_ADDRESS_DRAFT
  );
  const [errors, setErrors] = useState<Partial<Record<ErrorField, string>>>({});
  const [confirmDefault, setConfirmDefault] = useState(false);

  const clearError = (field: ErrorField) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const changeRecipient = (field: RecipientField, value: string) => {
    setRecipient((prev) => ({ ...prev, [field]: value }));
    clearError(field);
  };

  const changePlace = (next: AddressDraft, field: AddressDraftField) => {
    setPlace(next);
    clearError(field);
  };

  const handleSave = () => {
    const nextErrors: Partial<Record<ErrorField, string | undefined>> = {
      recipient_name: recipient.recipient_name.trim() ? undefined : 'Nama penerima wajib diisi.',
      phone_number: recipient.phone_number.trim() ? undefined : 'Nomor telepon wajib diisi.',
      ...addressDraftErrors(place),
    };
    const placeBody = addressPlaceOf(place);
    if (!placeBody || Object.values(nextErrors).some(Boolean)) {
      setErrors(nextErrors);
      return;
    }
    const body = {
      ...placeBody,
      recipient_name: recipient.recipient_name.trim(),
      phone_number: recipient.phone_number.trim(),
    };
    saveAddress.mutate(
      { id: address?.id, body },
      {
        onSuccess: () => {
          showToast({
            type: 'success',
            title: 'Alamat disimpan',
            message: 'Alamat pengiriman berhasil disimpan.',
          });
          navigate(-1);
        },
        onError: (error) => {
          const fieldError = fieldErrorOf(error);
          const field =
            fieldError &&
            (fieldError.field === 'recipient_name' || fieldError.field === 'phone_number'
              ? fieldError.field
              : addressDraftFieldOf(fieldError.field));
          if (fieldError && field) {
            setErrors({ [field]: fieldError.message });
          } else {
            showToast({ type: 'error', title: 'Gagal menyimpan alamat', message: error.message });
          }
        },
      }
    );
  };

  const handleSetDefault = () => {
    if (!address) return;
    setDefaultAddress.mutate(address.id, {
      onSuccess: () => {
        showToast({
          type: 'success',
          title: 'Alamat utama diubah',
          message: 'Alamat ini sekarang menjadi alamat utama.',
        });
      },
      onSettled: () => setConfirmDefault(false),
    });
  };

  return (
    <>
      <WrapperMobile>
        <div className="shadowCard p-3 flex flex-col gap-3">
          <div className="inline-flex w-full gap-3 items-center">
            <figure className="shrink-0">
              <img src={LocIcon} alt="icon lokasi" loading="eager" className="w-auto h-auto" />
            </figure>
            <div className="flex-1 min-w-0">
              <p className="title2medium truncate">Pengaturan Alamat Pengiriman</p>
              <p className="reg12 text-text-grey truncate">Atur alamat pengiriman belanjaan.</p>
            </div>
          </div>
          <InputMobile
            label="Nama Lengkap"
            name="recipient_name"
            placeholder="Masukkan nama"
            autoComplete="name"
            maxLength={100}
            value={recipient.recipient_name}
            onChange={(e) => changeRecipient('recipient_name', e.target.value)}
            error={errors.recipient_name}
          />
          <InputMobile
            label="Nomor Handphone / WhatsApp"
            name="phone_number"
            placeholder="Masukkan nomor telepon"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            maxLength={20}
            value={recipient.phone_number}
            onChange={(e) => changeRecipient('phone_number', e.target.value)}
            error={errors.phone_number}
          />
          <AddressFields value={place} onChange={changePlace} errors={errors} />
        </div>
        <div className="flex flex-col gap-3 w-full">
          <Button
            customButton="btn-fill-blue"
            onClick={handleSave}
            disabled={saveAddress.isPending}
          >
            <Save />
            <span className="title2medium text-white">Simpan Alamat</span>
          </Button>
          {address && !address.is_default && (
            <Button customButton="btn-fill-white" onClick={() => setConfirmDefault(true)}>
              <span className="title2medium">Ubah sebagai alamat utama</span>
            </Button>
          )}
        </div>
      </WrapperMobile>

      <ConfirmModal
        open={confirmDefault}
        title="Ubah sebagai alamat utama?"
        message="Apakah Anda yakin ingin mengubah menjadi alamat utama?"
        confirmLabel="Ya, Ubah"
        variant="primary"
        loading={setDefaultAddress.isPending}
        onConfirm={handleSetDefault}
        onClose={() => setConfirmDefault(false)}
      />
    </>
  );
};

export default SettingAddress;
